import { OPERATORS_REQUIRING_TWO_VALUES } from "../../../services/utils";
import { Query, QuerySortOrder, QuerySpecMatch, QueryTerm, QueryTermOp } from "./query";
import { v4 as uuidv4 } from "uuid";

export type QueryInputTermOp = "=" | ">" | ">=" | "<" | "<=" | "!=" | "!!" | "!" | "~" | "=~";

export type QueryInputTermType = "property" | "rel" | "tag";

export interface QueryInputTerm {
  id: string;
  op: QueryInputTermOp;
  termType: QueryInputTermType;
  key: string;
  value: string;
}

export interface QueryInput {
  isEnabled: boolean;
  match: QuerySpecMatch;
  terms: QueryInputTerm[];
  sort: {
    isEnabled: boolean;
    by: string;
    order: QuerySortOrder;
  };
}

export function getDefaultQueryInputTerm(): QueryInputTerm {
  return {
    id: uuidv4(),
    termType: "tag",
    key: "",
    op: "=",
    value: "",
  };
}

export function getDefaultQueryInput(): QueryInput {
  return {
    isEnabled: false,
    match: "all",
    terms: [],
    sort: {
      isEnabled: false,
      by: "",
      order: "asc",
    },
  };
}

export function inputToQuery(input: QueryInput, kind: string): Query {
  const res: Query = {
    kind: kind as any,
    spec: {
      match: input.match,
      terms: input.terms.map((t) => {
        const op: QueryTermOp = t.op === "!" ? "!exists" : t.op === "!!" ? "exists" : t.op;
        let res: QueryTerm = {} as any;
        if (t.termType === "property") {
          res = { property: t.key, value: t.value, op: op };
        }
        if (t.termType === "rel") {
          res = { rel: t.key, value: t.value, op: op };
        }
        if (t.termType === "tag") {
          res = { tag: t.key, value: t.value, op: op };
        }
        if (!OPERATORS_REQUIRING_TWO_VALUES.includes(op)) {
          delete res.value;
        }
        return res;
      }),
    },
  };
  if (input.sort.isEnabled) {
    res.spec.sort = {
      by: input.sort.by,
      order: input.sort.order,
    };
  }
  return res;
}

export function queryToInput(query?: Query): QueryInput {
  return {
    isEnabled: (query?.spec?.terms || []).length > 0,
    match: query?.spec?.match || "all",
    terms: (query?.spec?.terms || []).map((t) => {
      let op: QueryInputTermOp = t.op === "exists" ? "!!" : t.op === "!exists" ? "!" : t.op;
      if (!op) {
        op = "=";
      }
      if (t.property) {
        return { id: uuidv4(), termType: "property", key: t.property!, value: String(t.value || ""), op: op };
      }
      if (t.rel) {
        return { id: uuidv4(), termType: "rel", key: t.rel!, value: String(t.value || ""), op: op };
      }
      return { id: uuidv4(), termType: "tag", key: t.tag!, value: String(t.value || ""), op: op };
    }),
    sort: {
      isEnabled: !!query?.spec?.sort,
      by: query?.spec?.sort?.by || "",
      order: query?.spec?.sort?.order || "asc",
    },
  };
}

export const QueryTermOperatorOptions: { label: string; value: QueryInputTermOp }[] = [
  { label: "Equals", value: "=" },
  { label: "Not Equals", value: "!=" },
  { label: "Exists", value: "!!" },
  { label: "Not Exists", value: "!" },
  { label: "Contains", value: "~" },
  { label: "Regex", value: "=~" },
  { label: "Greater", value: ">" },
  { label: "Greater or Equals", value: ">=" },
  { label: "Less", value: "<" },
  { label: "Less or Equals", value: "<=" },
];
export const QueryInputTermTypeOptions = [
  { label: "Tag", value: "tag" },
  { label: "Property", value: "property" },
  { label: "Rel", value: "rel" },
];
export const QueryMatchOptions = [
  { label: "All", value: "all" },
  { label: "Any", value: "any" },
  { label: "None", value: "none" },
];
